<template>
  <v-container fluid>
    <v-snackbar v-model="snackbar.model" top :color="snackbar.color">
      {{ snackbar.detail }}
    </v-snackbar>
    <TransactionDetail ref="itemDetail"
      @detail-display="loadList" @update-detail="loadList" @show-message="onMessageDisplay" />

    <StatementUploader
      @statement-uploaded="loadList" @show-messsage="onMessageDisplay" ref="stUploader"/>

    <v-row no-gutters class="mt-4">
      <v-col cols="12" sm="6" md="3">
        <v-select dense label="Select entity" v-model="entityId" :loading="loading"
          @change="onEntityId" :items="entities" outlined></v-select>
      </v-col>
      <template v-if="entityId">
        <v-col cols="12" sm="6" md="3">
          <v-select dense label="Select bank account" v-model="bankAccount"
            @change="onBankAccount" :items="bankAccounts" outlined></v-select>
        </v-col>
      </template>
    </v-row>
    <v-row no-gutters>
      <template v-if="entityId">
        <v-col cols="12" sm="6" md="3">
          <date-text-field label="Start date" :model.sync="filters.start_date" outlined></date-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <date-text-field label="End date" :model.sync="filters.end_date" outlined></date-text-field>
          <!--v-text-field dense label="End date" type="date" v-model="filters.end_date" outlined></v-text-field-->
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select dense outlined label="Category Type" v-model="filters.category_type"
            clearable :items="categoryTypes"></v-select>
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-btn class="ml-2" rounded block outlined color="info" @click="loadList">Filter</v-btn>
        </v-col>
      </template>
    </v-row>
    <v-divider/>
    <v-row :no-gutters="$vuetify.breakpoint.smAndDown">
      <v-tabs v-show="entityId" grow v-model="tabs">
        <v-tab>Transactions</v-tab>
        <v-tab>Reports</v-tab>
        <v-tab-item>
          <v-container>
            <v-row v-if="entityId">
              <v-col cols="6" sm="4">
                <v-btn small rounded color="success" @click="showEmptyDetail('D')">
                  Add income
                </v-btn>
                <v-btn class="ml-4" small rounded color="warning" @click="showEmptyDetail('W')">
                  Add expense
                </v-btn>
              </v-col>
              <v-col cols="12" sm="8" align="right">
                <v-btn small rounded class="info" color="lighten-1" @click="openUploader">
                  <v-icon>add</v-icon> Upload transactions
                </v-btn>
                <v-btn small rounded class="info ml-4" color="lighten-1" @click="generateReport">
                  Generate report
                </v-btn>
              </v-col>
              <v-col :cols="12" class="entity-list">
                <TransactionList
                  @load-detail="loadDetail"
                  @load-balance="loadBankAccount"
                  @show-message="onMessageDisplay"
                  ref="itemList"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <Report ref="report"/>
        </v-tab-item>
      </v-tabs>
    </v-row>
  </v-container>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { namespace, Action } from 'vuex-class'

import TransactionList from './TransactionList'
import TransactionDetail from './TransactionDetail'
import StatementUploader from '@/components/cashbook/StatementUploader'
import Report from '@/components/report'
import DateTextField from '@/components/DateTextField'

import { Api } from '@/services/Api'
import EntityService from '@/services/EntityService'
import BankAccountService from '@/services/BankAccountService'

const TransactionModule = namespace('transaction')
const BankAccountModule = namespace('bankaccount')

@Component({
  components: {
    DateTextField,
    TransactionList,
    TransactionDetail,
    StatementUploader,
    Report
  }
})
export default class Cashbook extends Vue {
  snackbar = { model: false }
  tabs = 0
  entityId = null
  entities = []
  loading = false
  bankAccounts = []
  bankAccount = null
  menus = {
    startDate: null,
    endDate: null
  }
  filters = {
    start_date: null,
    end_date: null,
    category_type: null
  }
  categoryTypes = []

  @Action setEntity
  @Action setPageTitle
  @TransactionModule.Action setCategoryOptions
  @TransactionModule.Action setAccountOptions
  @BankAccountModule.Action setBankOptions
  @BankAccountModule.Action setAccountTypeOptions
  @TransactionModule.Getter fields

  updateOptions (url, callback, prepare) {
    this.loading = true
    Api().get(url)
      .then(resp => {
        const data = (resp.data.results || resp.data).map(item => {
          if (prepare === undefined) {
            return {
              text: item.name,
              value: item.id
            }
          } else {
            return prepare(item)
          }
        })
        callback(data)
      })
      .finally(() => {
        this.loading = false
      })
  }

  onEntityId (val) {
    this.$nextTick(() => {
      EntityService.get(val)
        .then(resp => {
          this.setEntity(resp.data.results || resp.data)
          this.loadList()
          this.loadBankAccount()
          this.tabs = 0
        })
    })
  }

  onBankAccount (val) {
    this.$nextTick(() => {
      this.loadList()
      if (this.$refs.report) {
        this.$refs.report.setEntityId(this.entityId)
      }
      this.tabs = 0
    })
  }

  loadList () {
    this.$nextTick(() => {
      this.$refs.itemList.load(this.entityId, this.bankAccount, this.filters)
      this.tabs = 0
    })
  }

  loadDetail (item) {
    this.$nextTick(() => {
      this.$refs.itemDetail.load(item.id)
    })
  }

  loadBankAccount () {
    BankAccountService.list(this.entityId)
      .then(resp => {
        let total = 0.0
        const accounts = []
        this.bankAccounts = (resp.data.results || resp.data).map(v => {
          total += parseFloat(v.balance)
          accounts.push({ text: v.name, value: v.id })
          return {
            text: `${v.name} (balance: $${v.balance})`,
            value: v.id
          }
        })
        this.setAccountOptions(accounts)
        this.$refs.stUploader.setBankAccounts(accounts)
        this.bankAccounts.splice(0, 0, {
          text: `Total balance: $${total.toFixed(2)}`,
          value: 0
        })
        this.bankAccount = 0
      })
  }

  mounted () {
    this.setPageTitle('Cashbook')
    this.loading = true
    EntityService.list_options()
      .then(resp => {
        this.entities = (resp.data.results || resp.data).map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
      .finally(() => {
        this.loading = false
      })

    this.updateOptions('/cashbook/bank/', this.setBankOptions)
    this.updateOptions('/cashbook/bank-account-type/', this.setAccountTypeOptions)
    this.updateOptions('/cashbook/category/', this.setCategoryOptions, (item) => {
      return {
        text: `${item.name} (${item.category_type.name} | ${item.category_group.name})`,
        value: item.id,
        gst_registered: item.gst_registered
      }
    })
    this.updateOptions('/cashbook/category-type/', (data) => { this.categoryTypes = data })
  }

  showEmptyDetail (dw) {
    this.$nextTick(() => {
      this.$refs.itemDetail.open(dw)
    })
  }

  openUploader () {
    this.$nextTick(() => {
      this.loadBankAccount()
      this.$refs.stUploader.open()
    })
  }

  generateReport () {
    this.tabs = 1
    setTimeout(() => {
      this.$refs.report.generate(this.entityId, this.bankAccount, this.filters)
    }, 100)
  }

  onMessageDisplay (data) {
    this.snackbar = {
      model: true,
      detail: data.message + (data.error !== undefined ? `: ${data.error}` : ''),
      color: data.error === undefined ? 'success' : 'error'
    }
  }
}
</script>
